/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  USAGE ANALYTICS — the closed vocabularies. [ANALYTICS]
 *
 *  ⚠️ A MODULE WITH NO IMPORTS, `dutyNotification.constant.ts`'s reason: the capture
 *  middleware, the rollup, the read API and the web page all key on these lists, and
 *  `@pams/shared/constants` is the one barrel that drags in neither zod nor luxon.
 *
 *  ⚠️ EVERY LIST HERE IS STORED. A window, a bucket boundary or a kind is written into Redis
 *  keys and into `analytics_buckets` documents, so reordering or renaming a member is a data
 *  change rather than a refactor — a histogram re-bucketed in place would sum counts taken
 *  against two different boundaries.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * The windows the Usage Analytics page offers. `30m` is read live from Redis; the others
 * read the rolled-up tiers, which is why they lag by about two minutes (the page's own limit sentence).
 */
export const ANALYTICS_WINDOWS = ['30m', '1d', '7d', '30d', '90d'] as const;
export type AnalyticsWindow = (typeof ANALYTICS_WINDOWS)[number];

export const ANALYTICS_DEFAULT_WINDOW: AnalyticsWindow = '7d';

/**
 * ⚠️ THE UPPER BOUNDS, IN MILLISECONDS, OF ELEVEN LATENCY BUCKETS — AND THERE ARE TWELVE.
 *
 * A request slower than the last boundary lands in a TWELFTH, implicit `+Inf` bucket, so every
 * stored `buckets` array has `ANALYTICS_LATENCY_BUCKETS_MS.length + 1` entries. The `+Inf`
 * bucket is not a member of this tuple because it has no finite bound, and `Infinity` does not
 * survive the wire — `JSON.stringify(Infinity)` is `null`.
 */
export const ANALYTICS_LATENCY_BUCKETS_MS = [5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10000] as const;

/**
 * ⚠️ THE FIELD A CAPPED MINUTE COUNTS EVERY ADDRESS PAST ITS CAP UNDER — `~other`, and
 * `~other|<kind>` for a refusal. Written by the capture and read by the rollup, the read and the
 * geo lookup, so it is declared ONCE: a second literal that drifted would rank `~other` as a real
 * address in the top IPs and in the clusters, with nothing to say so. `~` cannot begin an IPv4
 * address, an IPv6 key or `unknown`, so it collides with no key the capture writes.
 */
export const ANALYTICS_OTHER_KEY = '~other';

/**
 * Why a request was refused, as the capture classifies it from the status, the route and the
 * auth middleware's rejection reason. None of these changes a response body: the kind is
 * recorded beside the refusal, never sent with it.
 */
export const ANALYTICS_AUTH_FAILURE_KINDS = [
  'otp_locked',
  'otp_send_limited',
  'rate_limited',
  'otp_code_rejected',
  'refresh_rejected',
  'token_expired',
  'token_invalid',
  'token_missing',
  'token_refused',
  'wrong_surface',
  'otp_refused',
  'forbidden',
  'otp_unknown_identifier',
] as const;
export type AnalyticsAuthFailureKind = (typeof ANALYTICS_AUTH_FAILURE_KINDS)[number];

/**
 * ⚠️ AN ACCESS TOKEN LIVES THIRTY MINUTES, SO EVERY SIGNED-IN OFFICER PRODUCES AN EXPIRED-TOKEN
 * 401 IN THE ORDINARY COURSE OF A SHIFT. Counted, and kept out of the cluster ranking: ranked
 * alongside the rest, the busiest police station would read as the most suspicious address.
 */
export const ANALYTICS_ROUTINE_AUTH_FAILURES = ['token_expired'] as const satisfies readonly AnalyticsAuthFailureKind[];

/** Security and usage events counted where they already happen, beside the request capture. */
export const ANALYTICS_SIGNAL_KINDS = ['reuse_revocation', 'otp_ban', 'sign_in_dashboard', 'sign_in_officer'] as const;
export type AnalyticsSignalKind = (typeof ANALYTICS_SIGNAL_KINDS)[number];

/**
 * ⚠️ THE THREE TIERS `analytics_buckets` HOLDS, FINEST FIRST — AND ALSO THE SERIES GRAIN A
 * WINDOW IS DRAWN AT. Stored as the `grain` column, so this is a data vocabulary: an hour is an
 * IST hour (it starts at :30 UTC) and a day an IST day (it starts at 18:30 UTC the day before).
 */
export const ANALYTICS_GRAINS = ['minute', 'hour', 'day'] as const;
export type AnalyticsGrain = (typeof ANALYTICS_GRAINS)[number];

/**
 * A route is ranked by its 95th percentile only once it has this many requests in the window.
 * Below it the percentile is a handful of calls, and the slowest route would be whichever one
 * somebody opened once over a slow connection. Named here so the page can say so.
 */
export const ANALYTICS_SLOW_ROUTE_MIN_REQUESTS = 20;

/**
 * An address — or an IPv4 /24 — is listed as a cluster only past this many NON-routine refusals
 * in the window. Routine ones (`ANALYTICS_ROUTINE_AUTH_FAILURES`) never count toward it.
 */
export const ANALYTICS_CLUSTER_MIN_FAILURES = 10;
